import React, {useState, useEffect, useMemo, useRef} from 'react';
import { ScrollView, TextInput, TouchableOpacity, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Toggle from '../../../../components/Toggle';
import Icon from '../../../../components/IcomoonIcon';
import SvgIcon from '../../../../components/Icon/Icon';
import Header from '../../../../navigation/Header';
import { useForm } from 'react-hook-form';
import Input from '../../../../components/Forms/Input';
import Select from '../../../../components/Forms/Select';
import IconButton from '../../../../components/IconButton';
import api from '../../../../utils/api';
import { useQuery } from '@tanstack/react-query';

import { useToast } from 'react-native-toast-notifications';
import UnitFeeLevelSelector from '../Fees/Components/UnitFeeLevelSelector';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';
import useCommunitySettings from '../../../../hooks/useCommunitySettings';
import BankAccountModal from './BankAccountModal';


const TOLERANCE_OPTIONS = [
  { label: '5 días', value: 5 },
  { label: '10 días', value: 10 },
  { label: '15 días', value: 15 },
  { label: '20 días', value: 20 },
];

const PENALTY_OPTIONS = [
  { label: '5%', value: 5 },
  { label: '10%', value: 10 },
  { label: '15%', value: 15 },
  { label: '20%', value: 20 },
];

const ADVANCE_OPTIONS = [
  { label: 'Sin descuento', value: 0 },
  { label: '-5%', value: 5 },
  { label: '-10%', value: 10 },
  { label: '-15%', value: 15 },
  { label: '-20%', value: 20 },
];


// Fila sobre fondo morado: etiqueta a la izquierda + selector (píldora morada, texto/chevron blancos).
function HeaderSelectRow({ label, value, items, onChange, disabled }) {
  return (
    <View style={tw`row justify-between items-center mb-3`}>
      <Text style={tw`text-white text-base`}>{label}</Text>
      <View style={tw`w-44`}>
        <Select
          value={value}
          items={items}
          onChange={onChange}
          disabled={disabled}
          iconColor='white'
          style={{
            wrapper: tw`mb-0`,
            input: tw`bg-[#632584] rounded-lg px-4 py-2.5 text-white font-600`,
          }}
        />
      </View>
    </View>
  );
}


function FeeLevelsSettings({ levels, communityId, onLevelsSaved }){
  const { control, reset, formState: {isDirty, dirtyFields}, handleSubmit } = useForm();
  const [showNewLevel, setShowNewLevel] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast()

  useEffect(() => {
    const _levels = levels?.map(level => {
      return {
        ...level,
        amount: `${level.amount}`
      }
    })

    reset({levels: _levels})
  }, [levels]);


  function renderRow(index){
    return (
      <View key={index} style={tw`row gap-x-3`}>
        <Input
          control={control}
          name={`levels[${index}].name`}
          rules={{required: true}}
          style={{ container: tw`flex-1 mb-2`, subcontainer: `bg-white border border-gray-200`, input: `py-2` }}
        />

        <View style={tw`row`}>
          <Input
            control={control}
            name={`levels[${index}].amount`}
            rules={{required: true}}
            placeholder=''
            style={{ container: tw`mb-2`, subcontainer: `bg-white border border-gray-200`, input: `w-28 pl-9 py-2` }}
            keyboardType="numeric"
          />
          <View style={tw`absolute left-4 top-0 h-12 justify-center`}>
            <Text style={tw`text-gray-500 font-bold`}>$</Text>
          </View>
        </View>

        <TouchableOpacity style={tw`w-10 items-center justify-center`}>
          <Icon name='close' size={18} color={tw.color('red')} />
        </TouchableOpacity>
      </View>
    )
  }

  function submitForm(data){
    const changedLevels = []

    dirtyFields.levels?.forEach((field, index) => {
      if(!! field) changedLevels.push(data.levels[index])
    })


    setSubmitting(true)

    api.post(`/communities/${communityId}/fees/types`, { types: changedLevels })
    .then((response) => {
      toast.show("Tipos de cuota guardados", {type: "success"})
      setSubmitting(false)
      onLevelsSaved?.()
    })
    .catch((error) => {
      console.error(error);
      setSubmitting(false)
    })
  }


  return (
    <View style={tw`px-3`}>
      {
        levels?.map((level, i) =>
          renderRow(i)
        )
      }

      {
        showNewLevel &&
          renderRow(levels ? levels.length : 0)
      }

      {
        ! isDirty ?
          <TouchableOpacity
            style={tw`border-2 border-dashed border-primary rounded-2xl px-4 py-3 row items-center justify-center mt-2`}
            onPress={() => setShowNewLevel(true)}
          >
            <Icon name='add1' size={18} color={tw.color('primary')} style={tw`mr-2`} />
            <Text style={tw`text-primary font-500`}>Crear variante de cuota</Text>
          </TouchableOpacity>
        :
          <IconButton
            onPress={handleSubmit(submitForm)}
            disabled={submitting}
            title={ submitting ? "Guardando" : "Guardar"}
            style={tw`bg-white border border-secondary mt-2 border-[0.45]`}
            textColor={'secondary'}
          />
      }
    </View>
  )
}


function UnitRow({ onPress, index, item, rightComponent }){
  return (
    <TouchableOpacity
      style={tw.style(`row justify-between px-4 h-15`, index % 2 == 1 && `bg-gray-100`)}
      disabled={!onPress}
      onPress={onPress}
    >
      <View style={tw`flex-1`}>
        <Text style={tw`text-secondary`}>{item.name}</Text>

        {
          item.owners?.length > 0 &&
            <Text style={tw`text-gray-400`}>{item.owners[0].name}</Text>
        }
      </View>

      <View style={tw` items-end`}>
        {rightComponent}
      </View>
    </TouchableOpacity>
  )
}


function MainFeeInput({ feeTypes, communityId, onLevelSaved }){
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputRef = useRef<any>()
  const toast = useToast()


  const mainFeeType = useMemo(() => {
    return feeTypes?.find(feeType => feeType.type === "main" && ! feeType.is_variant)
  }, [feeTypes])

  useEffect(() => {
    setValue(mainFeeType?.amount)
  }, [mainFeeType?.amount]);


  function submitChange() {
    const changedTypes = feeTypes.map((feeType, index) => {
      const _feeType = {...feeType}
      if (feeType.type == "main"){
        _feeType.amount = value
      }
      return _feeType
    })


    setSubmitting(true)

    api.post(`/communities/${communityId}/fees/types`, { types: changedTypes })
      .then((response) => {
        toast.show("Costo de cuota actualizado", { type: "success" })
        setSubmitting(false)
        onLevelSaved?.()
      })
      .catch((error) => {
        console.error(error);
        setSubmitting(false)
      })
  }


  return (
    <View style={tw`row justify-center border-b border-white/20 py-3 mb-3`}>
      <View style={tw.style(`row items-center rounded-lg px-3 py-1`, editing && `bg-white/10`)}>
        <Text style={tw`text-red text-3xl font-600 mr-1`}>$</Text>

        <TextInput
          value={`${value ?? ''}`}
          style={[tw`text-white text-4xl font-500 text-center p-0`, { height: 48, minWidth: 90 }]}
          keyboardType='numeric'
          ref={inputRef}
          onChangeText={setValue}
          editable={editing}
        />
      </View>

      <TouchableOpacity
        style={tw`absolute right-4 top-0 bottom-0 justify-center`}
        disabled={submitting}
        onPress={() => {
          if(!editing){
            setEditing(true)
            inputRef.current?.focus()
          }else{
            setEditing(false)
            inputRef.current?.blur()
            submitChange()
          }
        }}
      >
        {
          submitting ?
            <Text style={tw`text-primary/50`}>{"Guardando..."}</Text>
          :
            <Text style={tw`text-primary`}>{editing ? "Guardar" : "Editar"}</Text>
        }
      </TouchableOpacity>
    </View>
  )
}


interface BankAccount {
  uuid: string;
  name: string;
  bank: string;
  account_number: string;
  clabe: string;
  owner_name: string;
}

function BankAccountCard({ account, onEdit, onDelete }: { account: BankAccount; onEdit: () => void; onDelete: () => void }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <View style={tw`bg-white rounded-2xl px-4 py-4 mb-3 border border-gray-200`}>
      <View style={tw`row justify-between mb-2`}>
        <Text style={tw`text-secondary font-600`}>{account.name}</Text>
        <TouchableOpacity onPress={() => setShowMenu(!showMenu)}>
          <SvgIcon name='more' size={20} color={tw.color('gray-500')} />
        </TouchableOpacity>
      </View>

      {showMenu && (
        <View style={tw`absolute right-4 top-10 bg-white rounded-lg shadow-lg z-10 border border-gray-200`}>
          <TouchableOpacity style={tw`px-4 py-2`} onPress={() => { setShowMenu(false); onEdit(); }}>
            <Text>Editar</Text>
          </TouchableOpacity>
          <TouchableOpacity style={tw`px-4 py-2`} onPress={() => { setShowMenu(false); onDelete(); }}>
            <Text style={tw`text-red`}>Eliminar</Text>
          </TouchableOpacity>
        </View>
      )}

      <View style={tw`row mb-1`}>
        <Text style={tw`text-gray-500 w-16`}>Banco</Text>
        <Text style={tw`text-gray-700`}>{account.bank}</Text>
      </View>
      <View style={tw`row mb-1`}>
        <Text style={tw`text-gray-500 w-16`}>Cuenta</Text>
        <Text style={tw`text-gray-700`}>{account.account_number}</Text>
      </View>
      <View style={tw`row mb-1`}>
        <Text style={tw`text-gray-500 w-16`}>CLABE</Text>
        <Text style={tw`text-gray-700`}>{account.clabe}</Text>
      </View>
      <View style={tw`row`}>
        <Text style={tw`text-gray-500 w-16`}>Titular</Text>
        <Text style={tw`text-gray-700`}>{account.owner_name}</Text>
      </View>
    </View>
  );
}


export default function MaintenanceSettingsScreen({ navigation }) {
  const { communityId } = useActiveAdminCommunity()
  const { updateSettings, getSettingValue, isUpdating } = useCommunitySettings()
  const toast = useToast()

  const [showBankAccountModal, setShowBankAccountModal] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccount | null>(null);

  const { isLoading, data: units, refetch: refetchUnits } = useQuery({
    queryKey: ['community.units', communityId],
    queryFn: () => api.get(`/communities/${communityId}/units?with=fee_type`).then(response => response.data),
  })


  const { isLoading: loadingLevels, data: feeTypes, refetch: refetchLevels } = useQuery({
    queryKey: ['community.fees.levels', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees/types`).then(response => response.data),
  })

  const { data: bankAccounts, refetch: refetchBankAccounts } = useQuery({
    queryKey: ['community.bank-accounts', communityId],
    queryFn: () => api.get(`/communities/${communityId}/bank-accounts`).then(response => response.data),
  })

  const mainteinanceFeeTypes = useMemo(() => {
    return feeTypes?.filter(feeType => feeType.type === "main")
  }, [feeTypes])

  const variantsFeeTypes = useMemo(() => {
    return feeTypes?.filter(feeType => feeType.type === "main" && feeType.is_variant)
  }, [feeTypes])

  const handleDeleteBankAccount = async (account: BankAccount) => {
    try {
      await api.delete(`/communities/${communityId}/bank-accounts/${account.uuid}`);
      toast.show("Cuenta eliminada", { type: "success" });
      refetchBankAccounts();
    } catch (error: any) {
      toast.show(error.response?.data?.message || "Error al eliminar", { type: "warning" });
    }
  };


  return (
    <>
      <Header style={`mb-0 bg-secondary rounded-b-0`} showBackButton backButtonColor='white'>
        <Text style={tw`text-xl font-600 text-white`}>Mantenimiento</Text>
      </Header>

      <ScrollView style={tw`bg-[#F3F2F3] flex-1`}>

        {/* Cuota principal + parámetros (sobre fondo morado, se desplaza con la página) */}
        <View style={tw`bg-secondary px-5 pt-1 pb-5 rounded-b-3xl mb-5`}>
          <Text style={tw`text-center text-white/80 text-sm mb-1`}>
            Cada primero de mes los residentes recibirán un recordatorio de pago
          </Text>

          <MainFeeInput
            communityId={communityId}
            feeTypes={feeTypes}
            onLevelSaved={() => {
              refetchLevels()
              refetchUnits()
            }}
          />

          {/* Tolerancia, Penalización y Pago adelantado */}
          <View style={tw`mt-1`}>
            <HeaderSelectRow
              label="Tolerancia"
              value={getSettingValue('fees.tolerance_days', 10)}
              items={TOLERANCE_OPTIONS}
              onChange={(value) => updateSettings('fees.tolerance_days', value)}
              disabled={isUpdating}
            />
            <HeaderSelectRow
              label="Penalización"
              value={getSettingValue('fees.penalty_percent', 10)}
              items={PENALTY_OPTIONS}
              onChange={(value) => updateSettings('fees.penalty_percent', value)}
              disabled={isUpdating}
            />
            <HeaderSelectRow
              label="Pago adelantado"
              value={getSettingValue('fees.advance_discount_percent', 0)}
              items={ADVANCE_OPTIONS}
              onChange={(value) => updateSettings('fees.advance_discount_percent', value)}
              disabled={isUpdating}
            />
          </View>
        </View>

        {/* Variación de costo */}
        <View style={tw`mb-4`}>
          <View style={tw`px-5 mb-3`}>
            <Text style={tw`text-secondary font-600 text-base`}>Variación de costo</Text>
            <Text style={tw`text-gray-500 text-sm mt-0.5`}>Define cuotas con un monto distinto para ciertas viviendas.</Text>
          </View>

          <FeeLevelsSettings
            levels={variantsFeeTypes}
            communityId={communityId}
            onLevelsSaved={() => {
              refetchLevels()
              refetchUnits()
            }}
          />
        </View>

        {/* Información para transferencias */}
        <View style={tw`mb-4`}>
          <View style={tw`px-5 mb-3`}>
            <Text style={tw`text-secondary font-600 text-base`}>Información para transferencias</Text>
            <Text style={tw`text-gray-500 text-sm mt-0.5`}>Cuentas bancarias donde tus residentes realizan sus pagos.</Text>
          </View>

          <View style={tw`px-3`}>
            {bankAccounts?.map((account: BankAccount) => (
              <BankAccountCard
                key={account.uuid}
                account={account}
                onEdit={() => {
                  setSelectedBankAccount(account);
                  setShowBankAccountModal(true);
                }}
                onDelete={() => handleDeleteBankAccount(account)}
              />
            ))}

            <TouchableOpacity
              style={tw`border-2 border-dashed border-primary rounded-2xl px-4 py-3 row items-center justify-center`}
              onPress={() => {
                setSelectedBankAccount(null);
                setShowBankAccountModal(true);
              }}
            >
              <Icon name='add1' size={18} color={tw.color('primary')} style={tw`mr-2`} />
              <Text style={tw`text-primary font-500`}>Agregar cuenta bancaria</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Envío de comprobantes */}
        <View style={tw`mx-3 mb-4`}>
          <View style={tw`bg-white rounded-2xl px-4 py-4`}>
            <View style={tw`row items-center justify-between mb-2`}>
              <Text style={tw`text-secondary font-600`}>Envío de comprobantes</Text>
              <Toggle
                value={getSettingValue('fees.payment_evidence_enabled', true)}
                onValueChange={(value) => updateSettings('fees.payment_evidence_enabled', value)}
                disabled={isUpdating}
              />
            </View>
            <Text style={tw`text-gray-500 text-sm`}>
              Permite que tus residentes puedan enviar sus comprobantes de pago. Al enviarlo, su adeudo aparecerá como "Pendiente de aprobación" hasta que la administración lo valide.
            </Text>
          </View>
        </View>

        {/* Habilitar pago digital */}
        <View style={tw`mx-3 mb-4`}>
          <View style={tw`bg-white rounded-2xl px-4 py-4`}>
            <Text style={tw`text-secondary font-600 mb-2`}>Habilitar pago digital</Text>
            <Text style={tw`text-gray-500 text-sm mb-4`}>
              Permite que tus residentes realicen los pagos desde la aplicación.{'\n'}
              Cada transacción genera una comisión.
            </Text>
            <IconButton
              title="Solicitar (Próximamente)"
              icon={null}
              disabled={true}
              onPress={() => {}}
              style={tw`self-stretch`}
            />
          </View>
        </View>

        {/* Viviendas (opcional - puedes quitarlo si es muy largo) */}
        {/* <View>
          <View style={tw`bg-gray-200 px-4 py-2 row justify-between mb-3`}>
            <Text style={tw`text-gray-500`}>Vivienda</Text>
            <Text style={tw`text-gray-500`}>Monto / Variante</Text>
          </View>

          <View>
            {
              units?.map((item, i) =>
                <UnitRow
                  key={item.uuid}
                  index={i}
                  item={item}
                  rightComponent={
                    <UnitFeeLevelSelector
                      unit={item}
                      feeTypes={mainteinanceFeeTypes}
                      communityId={communityId}
                      onFeeLevelUpdated={() => {
                        refetchUnits()
                      }}
                    />
                  }
                />
              )
            }

            {
              isLoading &&
                <Text style={tw`mt-10 text-gray-300 text-center`}>Cargando...</Text>
            }
          </View>
        </View> */}

        <View style={tw`h-10`} />
      </ScrollView>

      {/* Modal de cuenta bancaria */}
      {showBankAccountModal && (
        <BankAccountModal
          visible={showBankAccountModal}
          bankAccount={selectedBankAccount}
          onRequestClose={() => setShowBankAccountModal(false)}
          onSaved={() => {
            setShowBankAccountModal(false);
            refetchBankAccounts();
          }}
        />
      )}
    </>
  );
}
